import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AnalysesService } from '../../services';
import {
  AnalysesState,
  Analysis,
  CreateAnalysisRequest,
  EnhancedAnalysisRequest,
  AnalysisDetailsResponse,
  AnalysisQueryParams,
  LLMProvider,
} from '../../types/api/analyses';

// Initial state
const initialState: AnalysesState = {
  analyses: [],
  selectedAnalysis: null,
  analysisDetails: null,
  llmProviders: [],
  currentAnalysisId: null,
  processingAnalysis: false,
  loading: false,
  error: null,
};

export const fetchAnalyses = createAsyncThunk(
  'analyses/fetchAnalyses',
  async (params: AnalysisQueryParams = {}, { rejectWithValue }) => {
    try {
      const response = await AnalysesService.getAnalyses(params);
      return response.results || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch analyses');
    }
  }
);

export const fetchAnalysisDetails = createAsyncThunk(
  'analyses/fetchAnalysisDetails',
  async (analysisId: string, { rejectWithValue }) => {
    try {
      const details = await AnalysesService.getAnalysis(analysisId);
      return details;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch analysis details');
    }
  }
);

export const createAnalysis = createAsyncThunk(
  'analyses/createAnalysis',
  async (data: CreateAnalysisRequest, { rejectWithValue }) => {
    try {
      const analysis = await AnalysesService.createAnalysis(data);
      return analysis;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create analysis');
    }
  }
);

export const createEnhancedAnalysis = createAsyncThunk(
  'analyses/createEnhancedAnalysis',
  async (data: EnhancedAnalysisRequest, { rejectWithValue }) => {
    try {
      const analysis = await AnalysesService.createEnhancedAnalysis(data);
      return analysis;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create enhanced analysis');
    }
  }
);

export const deleteAnalysis = createAsyncThunk(
  'analyses/deleteAnalysis',
  async (analysisId: string, { rejectWithValue }) => {
    try {
      await AnalysesService.deleteAnalysis(analysisId);
      return analysisId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete analysis');
    }
  }
);

export const updateAnalysisStatus = createAsyncThunk(
  'analyses/updateAnalysisStatus',
  async ({ analysisId, status }: { analysisId: string; status: string }, { rejectWithValue }) => {
    try {
      const analysis = await AnalysesService.updateAnalysisStatus(analysisId, status);
      return analysis;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update analysis status');
    }
  }
);


export const exportAnalysis = createAsyncThunk(
  'analyses/exportAnalysis',
  async ({ analysisId, format }: { analysisId: string; format: 'pdf' | 'json' | 'csv' }, { rejectWithValue }) => {
    try {
      const blob = await AnalysesService.exportAnalysis(analysisId, format);
      return { blob, analysisId, format };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to export analysis');
    }
  }
);

export const bulkDeleteAnalyses = createAsyncThunk(
  'analyses/bulkDeleteAnalyses',
  async (analysisIds: string[], { rejectWithValue }) => {
    try {
      await AnalysesService.bulkDeleteAnalyses(analysisIds);
      return analysisIds;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete analyses');
    }
  }
);

export const bulkUpdateAnalysesStatus = createAsyncThunk(
  'analyses/bulkUpdateAnalysesStatus',
  async ({ analysisIds, status }: { analysisIds: string[]; status: string }, { rejectWithValue }) => {
    try {
      await AnalysesService.bulkUpdateStatus(analysisIds, status);
      return { analysisIds, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update analyses status');
    }
  }
);

// Analyses slice
const analysesSlice = createSlice({
  name: 'analyses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedAnalysis: (state, action: PayloadAction<Analysis | null>) => {
      state.selectedAnalysis = action.payload;
    },
    setCurrentAnalysisId: (state, action: PayloadAction<string | null>) => {
      state.currentAnalysisId = action.payload;
    },
    clearAnalysisDetails: (state) => {
      state.analysisDetails = null;
    },
    resetAnalysesState: () => initialState,
    updateAnalysisInList: (state, action: PayloadAction<Analysis>) => {
      const index = state.analyses.findIndex(a => a.analysis_id === action.payload.analysis_id);
      if (index !== -1) {
        state.analyses[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch analyses
    builder
      .addCase(fetchAnalyses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalyses.fulfilled, (state, action) => {
        state.loading = false;
        state.analyses = action.payload;
      })
      .addCase(fetchAnalyses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch analysis details
    builder
      .addCase(fetchAnalysisDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalysisDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.analysisDetails = action.payload;
        state.selectedAnalysis = action.payload.analysis;
      })
      .addCase(fetchAnalysisDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create analysis
    builder
      .addCase(createAnalysis.pending, (state) => {
        state.processingAnalysis = true;
        state.error = null;
      })
      .addCase(createAnalysis.fulfilled, (state, action) => {
        state.processingAnalysis = false;
        state.analyses.unshift(action.payload);
        state.currentAnalysisId = action.payload.analysis_id;
      })
      .addCase(createAnalysis.rejected, (state, action) => {
        state.processingAnalysis = false;
        state.error = action.payload as string;
      });

    // Create enhanced analysis
    builder
      .addCase(createEnhancedAnalysis.pending, (state) => {
        state.processingAnalysis = true;
        state.error = null;
      })
      .addCase(createEnhancedAnalysis.fulfilled, (state, action) => {
        state.processingAnalysis = false;
        state.analyses.unshift(action.payload);
        state.currentAnalysisId = action.payload.analysis_id;
      })
      .addCase(createEnhancedAnalysis.rejected, (state, action) => {
        state.processingAnalysis = false;
        state.error = action.payload as string;
      });

    // Delete analysis
    builder
      .addCase(deleteAnalysis.fulfilled, (state, action) => {
        state.analyses = state.analyses.filter(a => a.analysis_id !== action.payload);
        if (state.selectedAnalysis?.analysis_id === action.payload) {
          state.selectedAnalysis = null;
          state.analysisDetails = null;
        }
        if (state.currentAnalysisId === action.payload) {
          state.currentAnalysisId = null;
        }
      })
      .addCase(deleteAnalysis.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update analysis status
    builder
      .addCase(updateAnalysisStatus.fulfilled, (state, action) => {
        const index = state.analyses.findIndex(a => a.analysis_id === action.payload.analysis_id);
        if (index !== -1) {
          state.analyses[index] = action.payload;
        }
        if (state.selectedAnalysis?.analysis_id === action.payload.analysis_id) {
          state.selectedAnalysis = action.payload;
        }
      })
      .addCase(updateAnalysisStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      });


    // Bulk delete analyses
    builder
      .addCase(bulkDeleteAnalyses.fulfilled, (state, action) => {
        state.analyses = state.analyses.filter(a => !action.payload.includes(a.analysis_id));
      })
      .addCase(bulkDeleteAnalyses.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Bulk update analyses status
    builder
      .addCase(bulkUpdateAnalysesStatus.fulfilled, (state, action) => {
        const { analysisIds, status } = action.payload;
        state.analyses = state.analyses.map(analysis => 
          analysisIds.includes(analysis.analysis_id) 
            ? { ...analysis, status: status as any } 
            : analysis
        );
      })
      .addCase(bulkUpdateAnalysesStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedAnalysis,
  setCurrentAnalysisId,
  clearAnalysisDetails,
  resetAnalysesState,
  updateAnalysisInList,
} = analysesSlice.actions;

export default analysesSlice.reducer;